import React, { useState } from 'react'
import { StyleSheet, View, TouchableOpacity } from 'react-native'
import {
  Button,
  Dialog,
  Portal,
  TextInput,
  Text,
  HelperText,
  IconButton,
} from 'react-native-paper'
import { useTranslation } from 'react-i18next'
import Toast from 'react-native-root-toast'
import { MessageType } from '../../../../utils/message-type'
import * as Clipboard from 'expo-clipboard'
import { useTheme } from '../../../../hooks/theme.hook'
import { useCreateInvitationMutation } from '../../../../generated/graphql'
import * as Sentry from '@sentry/react-native'
import { copyInvitationWebLink } from '../../../../utils/invitation.utils'

interface CreateInvitationButtonProps {
  nodeId: string
  onCreated?: () => void
  iconOnly?: boolean
}

export const CreateInvitationButton: React.FC<CreateInvitationButtonProps> = ({
  nodeId,
  onCreated,
  iconOnly = false,
}) => {
  const { t } = useTranslation('nodes')
  const theme = useTheme()
  const [visible, setVisible] = useState(false)
  const [maxUsage, setMaxUsage] = useState('10')
  const [message, setMessage] = useState('')
  const [createdCode, setCreatedCode] = useState<string | null>(null)

  const [createInvitation, { loading }] = useCreateInvitationMutation()

  const showDialog = () => setVisible(true)
  const hideDialog = () => {
    setVisible(false)
    setCreatedCode(null)
    setMaxUsage('10')
    setMessage('')
  }

  const handleCreate = async () => {
    try {
      const result = await createInvitation({
        variables: {
          input: {
            nodeId,
            maxUsage: parseInt(maxUsage) || undefined,
            message: message || undefined,
          },
        },
      })

      const invitation = result.data?.createInvitation!

      setCreatedCode(invitation.code)
      Toast.show(t('invitation-created-successfully'), MessageType.info)
      onCreated?.()
      return
    } catch (e) {
      console.error(e)
      Toast.show((e as Error).message, MessageType.error)
      Sentry.captureException(e)
    }
  }

  const handleCopyLink = async () => {
    if (!createdCode) {
      return
    }
    await copyInvitationWebLink(createdCode, t('link-copied-to-clipboard'))
  }

  const maxUsageNumber = parseInt(maxUsage)
  const isValidMaxUsage =
    !maxUsage || (!isNaN(maxUsageNumber) && maxUsageNumber > 0)

  return (
    <>
      {iconOnly ? (
        <IconButton
          icon="link-plus"
          mode="contained-tonal"
          onPress={showDialog}
          size={24}
        />
      ) : (
        <Button mode="outlined" onPress={showDialog} icon="link-plus">
          {t('create-invitation-link')}
        </Button>
      )}

      <Portal>
        <Dialog visible={visible} onDismiss={hideDialog} style={styles.dialog}>
          <Dialog.Title>
            {createdCode
              ? t('invitation-link-created')
              : t('create-invitation-link')}
          </Dialog.Title>

          <Dialog.Content>
            {!createdCode ? (
              <View style={styles.form}>
                <TextInput
                  label={t('max-usage')}
                  value={maxUsage}
                  onChangeText={setMaxUsage}
                  keyboardType="number-pad"
                  mode="outlined"
                  placeholder="10"
                  error={!isValidMaxUsage}
                />
                <HelperText type={isValidMaxUsage ? 'info' : 'error'}>
                  {isValidMaxUsage
                    ? t('max-usage-help')
                    : t('max-usage-must-be-positive')}
                </HelperText>

                <TextInput
                  label={t('message-optional')}
                  value={message}
                  onChangeText={setMessage}
                  mode="outlined"
                  multiline
                  numberOfLines={3}
                  placeholder={t('welcome-message-placeholder')}
                />
              </View>
            ) : (
              <View style={styles.codeContainer}>
                <Text variant="bodyLarge" style={styles.successText}>
                  {t('invitation-code-created')}
                </Text>
                <TouchableOpacity
                  onPress={handleCopyLink}
                  style={styles.codeBox}
                  activeOpacity={0.7}
                >
                  <Text
                    variant="headlineMedium"
                    style={[styles.code, { color: theme.colors.primary }]}
                  >
                    {createdCode}
                  </Text>
                </TouchableOpacity>
                <Text variant="bodySmall" style={styles.helperText}>
                  {t('tap-code-to-copy')}
                </Text>
              </View>
            )}
          </Dialog.Content>

          <Dialog.Actions>
            {!createdCode ? (
              <>
                <Button onPress={hideDialog} disabled={loading}>
                  {t('cancel')}
                </Button>
                <Button
                  onPress={handleCreate}
                  loading={loading}
                  disabled={!isValidMaxUsage || loading}
                >
                  {t('create')}
                </Button>
              </>
            ) : (
              <>
                <Button onPress={handleCopyLink} icon="content-copy">
                  {t('copy-link')}
                </Button>
                <Button onPress={hideDialog}>{t('done')}</Button>
              </>
            )}
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </>
  )
}

const styles = StyleSheet.create({
  dialog: {
    maxWidth: 700,
    width: '90%',
    alignSelf: 'center',
  },
  form: {
    gap: 8,
  },
  codeContainer: {
    alignItems: 'center',
    gap: 16,
  },
  successText: {
    textAlign: 'center',
  },
  codeBox: {
    padding: 16,
    borderRadius: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: 'rgba(0, 0, 0, 0.2)',
  },
  code: {
    fontFamily: 'monospace',
    fontWeight: 'bold',
    letterSpacing: 2,
  },
  helperText: {
    textAlign: 'center',
    opacity: 0.7,
  },
})
